// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * AIGuidePage - Digitale nalatenschap
 * V47 UX-modernisering: kaartenstructuur per account, snelnavigatie, accentkleuren,
 * compacte waarschuwingen. Opslag/services ONGEWIJZIGD - elke sectie blijft één
 * newline-gescheiden string in DigitalEstateData (loadDigitalEstate/saveDigitalEstate).
 * Kaarten worden uit die string geparseerd en als dezelfde string teruggeschreven,
 * dus geen wijziging aan opslagstructuur en geen dataverlies.
 *
 * NB: er worden geen merklogo's gebruikt (handelsmerken/IP). Per kaart een neutraal
 * categorie-icoon + een gekleurd monogram uit de accountnaam.
 */
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import type { MutableRefObject } from 'react'
import {
  Mail, Users, Cloud, Smartphone, Tv, CreditCard, Wallet,
  TrendingUp, KeyRound, Globe, FileText, Plus, Trash2, Check, ArrowLeft, Landmark, CheckCircle2, Pencil } from 'lucide-react'
import { ROUTES }     from '@/lib/constants/routes'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { useReturnNav } from '@/lib/returnNav'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { InfoBlock }       from '@/components/ui/InfoBlock'
import { RegieCoachBlock } from '@/components/process/RegieCoachBlock'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { Textarea }        from '@/components/ui/Textarea'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { useAutosave }     from '@/hooks/useAutosave'
import { loadDigitalEstate, saveDigitalEstate, EMPTY_DIGITAL_ESTATE, type DigitalEstateData } from '@/services/wishes'
import { BrandLogo } from '@/components/brand/BrandLogo'

type Accent = 'green' | 'purple' | 'blue' | 'red' | 'amber' | 'emerald' | 'slate'

const ACCENT: Record<Accent, { chip: string; bar: string; ring: string }> = {
  green:   { chip: 'bg-green-50 text-green-700',     bar: 'bg-green-400',   ring: 'bg-green-100 text-green-700' },
  purple:  { chip: 'bg-purple-50 text-purple-700',   bar: 'bg-purple-400',  ring: 'bg-purple-100 text-purple-700' },
  blue:    { chip: 'bg-blue-50 text-blue-700',       bar: 'bg-blue-400',    ring: 'bg-blue-100 text-blue-700' },
  red:     { chip: 'bg-red-50 text-red-700',         bar: 'bg-red-400',     ring: 'bg-red-100 text-red-700' },
  amber:   { chip: 'bg-amber-50 text-amber-700',     bar: 'bg-amber-400',   ring: 'bg-amber-100 text-amber-700' },
  emerald: { chip: 'bg-emerald-50 text-emerald-700', bar: 'bg-emerald-400', ring: 'bg-emerald-100 text-emerald-700' },
  slate:   { chip: 'bg-slate-100 text-slate-700',    bar: 'bg-slate-300',   ring: 'bg-slate-100 text-slate-700' },
}

interface SectionDef {
  key:    keyof DigitalEstateData
  label:  string
  icon:   React.ReactNode
  accent: Accent
  short:  string
}

const SECTIONS: SectionDef[] = [
  { key: 'emailAccounts',         label: 'E-mailaccounts',              icon: <Mail className="h-4 w-4" />,        accent: 'green',   short: 'E-mail' },
  { key: 'socialMedia',           label: 'Social media',                icon: <Users className="h-4 w-4" />,       accent: 'purple',  short: 'Social' },
  { key: 'cloudStorage',          label: 'Cloudopslag',                 icon: <Cloud className="h-4 w-4" />,        accent: 'blue',    short: 'Cloud' },
  { key: 'devicesNotes',          label: 'Telefoon, tablet & computer', icon: <Smartphone className="h-4 w-4" />,  accent: 'green',   short: 'Apparaten' },
  { key: 'streamingServices',     label: 'Streamingdiensten',           icon: <Tv className="h-4 w-4" />,          accent: 'red',     short: 'Streaming' },
  { key: 'subscriptions',         label: 'Abonnementen (krant, tv, internet)', icon: <CreditCard className="h-4 w-4" />,  accent: 'slate',   short: 'Abonnementen' },
  { key: 'crypto',                label: 'Cryptocurrency',              icon: <Wallet className="h-4 w-4" />,      accent: 'amber',   short: 'Crypto' },
  { key: 'investments',           label: 'Beleggingen online',          icon: <TrendingUp className="h-4 w-4" />,  accent: 'emerald', short: 'Beleggingen' },
  { key: 'passwordManager',       label: 'Wachtwoordbeheer',            icon: <KeyRound className="h-4 w-4" />,    accent: 'amber',   short: 'Wachtwoorden' },
  { key: 'domainNames',           label: 'Domeinnamen & websites',      icon: <Globe className="h-4 w-4" />,       accent: 'blue',    short: 'Domeinen' },
  { key: 'websites',              label: 'Overheidsaccounts',           icon: <Landmark className="h-4 w-4" />,    accent: 'blue',    short: 'Overheid' },
  { key: 'importantInstructions', label: 'Overige digitale instructies', icon: <FileText className="h-4 w-4" />,   accent: 'slate',   short: 'Overig' },
]

function parseLine(line: string): { name: string; action: string } {
  const i = line.indexOf(' — ')
  if (i === -1) return { name: line.trim(), action: '' }
  return { name: line.slice(0, i).trim(), action: line.slice(i + 3).trim() }
}

// Afhandeling per dienst — opgeslagen als "Naam — Actie" in de bestaande string-velden.
// Geen datamodel-wijziging: alleen het actiegedeelte wordt gestructureerd.
type ActionType = 'keep' | 'cancel' | 'other' | ''
function parseAction(action: string): { type: ActionType; note: string } {
  const a = action.trim()
  const l = a.toLowerCase()
  if (!a) return { type: '', note: '' }
  if (l.startsWith('bewar') || l.startsWith('bewaar') || l.startsWith('behoud')) return { type: 'keep', note: '' }
  if (l.startsWith('opzeg') || l.startsWith('verwijder') || l.startsWith('annuleer')) return { type: 'cancel', note: '' }
  if (l.startsWith('anders')) return { type: 'other', note: a.replace(/^anders[:\s-]*/i, '').trim() }
  return { type: 'other', note: a } // bestaande vrije tekst blijft behouden onder "Anders"
}
function serializeAction(type: ActionType, note: string): string {
  if (type === 'keep')   return 'Bewaren'
  if (type === 'cancel') return 'Opzeggen'
  if (type === 'other')  return note.trim() ? `Anders: ${note.trim()}` : 'Anders'
  return ''
}
const norm = (x: string) => x.trim().toLowerCase()
function nameMatches(entryName: string, sugg: string): boolean {
  const e = norm(entryName), s = norm(sugg)
  return e === s || e.startsWith(s + ' ') || e.startsWith(s + ' (')
}

// Veelgebruikte diensten per categorie (alleen UI-suggesties; geen opslagwijziging).
// Categorieën zonder suggesties tonen uitsluitend "Dienst toevoegen".
const SUGGESTIONS: Partial<Record<keyof DigitalEstateData, string[]>> = {
  emailAccounts:     ['Gmail', 'Outlook / Hotmail', 'Yahoo Mail', 'iCloud Mail', 'ProtonMail'],
  socialMedia:       ['Facebook', 'Instagram', 'LinkedIn', 'X (Twitter)', 'TikTok', 'Snapchat', 'YouTube', 'Pinterest'],
  cloudStorage:      ['iCloud', 'Google Drive', 'Dropbox', 'OneDrive'],
  streamingServices: ['Netflix', 'Spotify', 'Disney+', 'Videoland', 'HBO Max', 'Amazon Prime Video', 'Apple TV+'],
  subscriptions:     ['Krant of tijdschrift', 'Tv-abonnement', 'Internet / provider', 'Tv en internet (Ziggo, KPN, e.d.)', 'Telefoonabonnement', 'Microsoft 365', 'Adobe Creative Cloud', 'Amazon Prime'],
  crypto:            ['Bitvavo', 'Coinbase', 'Binance', 'Ledger (hardware wallet)'],
  investments:       ['DEGIRO', 'BUX', 'Trade Republic', 'Meesman', 'Brand New Day'],
  passwordManager:   ['1Password', 'Bitwarden', 'LastPass', 'Apple Wachtwoorden', 'Google Wachtwoordbeheer'],
  websites:          ['DigiD', 'MijnOverheid', 'Belastingdienst', 'UWV', 'SVB'],
}

export function AIGuidePage({ embedded = false, saveRef }: { embedded?: boolean; saveRef?: MutableRefObject<(() => Promise<void>) | null> } = {}) {
  const { userId } = useAuthStore()
  const navigate   = useNavigate()
  const { target, label } = useReturnNav()
  const [data, setData]         = useState<DigitalEstateData>(EMPTY_DIGITAL_ESTATE)
  const [isLoading, setLoading] = useState(true)
  const [status, setStatus]     = useState<'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'>('IN_PROGRESS')
  const [customDraft, setCustomDraft] = useState<Record<string, string>>({})
  const [noteDraft, setNoteDraft]     = useState<Record<string, string>>({})  // V49-UAT-03: bewaart exact wat de gebruiker typt bij "Anders" (spaties blijven behouden)
  const [editingNote, setEditingNote] = useState<string | null>(null)         // V49-UAT-06: welke "Anders"-toelichting nu open staat (anders ingeklapt)

  useEffect(() => {
    if (!userId) return
    loadDigitalEstate(userId).then(loaded => {
      const d = loaded ?? EMPTY_DIGITAL_ESTATE
      setData(d); recalc(d)
      setLoading(false)
    })
  }, [userId])

  function recalc(d: DigitalEstateData) {
    const n = SECTIONS.filter(s => (d[s.key] || '').trim()).length
    const p = Math.min(100, Math.round((n / 6) * 100))
    setStatus(p === 0 ? 'NOT_STARTED' : p >= 100 ? 'COMPLETE' : 'IN_PROGRESS')
  }

  const save = useCallback(async (d: DigitalEstateData) => {
    if (!userId) return
    await saveDigitalEstate(userId, d)
  }, [userId])

  const { status: saveStatus, saveNow } = useAutosave({ data, onSave: save, enabled: !isLoading })

  // Wizard-embed: registreer saveNow zodat de wizard pending invoer flusht vóór navigatie
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])

  function setSection(key: keyof DigitalEstateData, lines: string[]) {
    setData(prev => { const n = { ...prev, [key]: lines.join('\n') }; recalc(n); return n })
  }
  function entriesFor(key: keyof DigitalEstateData): string[] {
    return (data[key] || '').split('\n').filter(l => l.trim().length > 0)
  }
  function toggleService(key: keyof DigitalEstateData, name: string, on: boolean) {
    const lines = entriesFor(key)
    const idx = lines.findIndex(l => nameMatches(parseLine(l).name, name))
    if (on && idx === -1) lines.push(`${name} — `)
    if (!on && idx !== -1) lines.splice(idx, 1)
    setSection(key, lines)
  }
  function setAction(key: keyof DigitalEstateData, name: string, type: ActionType, note: string) {
    const lines = entriesFor(key)
    const idx = lines.findIndex(l => nameMatches(parseLine(l).name, name))
    const realName = idx !== -1 ? parseLine(lines[idx]).name : name
    const serialized = serializeAction(type, note)
    const line = serialized ? `${realName} — ${serialized}` : `${realName} — `
    if (idx !== -1) lines[idx] = line; else lines.push(line)
    setSection(key, lines)
  }
  function addCustom(key: keyof DigitalEstateData) {
    const name = (customDraft[key] || '').trim()
    if (!name) return
    const lines = entriesFor(key)
    if (!lines.some(l => nameMatches(parseLine(l).name, name))) lines.push(`${name} — `)
    setSection(key, lines)
    setCustomDraft(prev => ({ ...prev, [key]: '' }))
  }
  function removeService(key: keyof DigitalEstateData, name: string) {
    const lines = entriesFor(key).filter(l => !nameMatches(parseLine(l).name, name))
    setSection(key, lines)
  }

  function scrollTo(key: string) {
    document.getElementById(`sec-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
      {!embedded && (
        <button onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}
          className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5">
          <ArrowLeft className="h-4 w-4" /> Terug naar dashboard
        </button>
      )}

      <ModuleShell title="Digitale nalatenschap"
        description="Welke digitale diensten gebruikt u?"
        status={status}
        actions={<SaveStatusBadge status={saveStatus} />}
      >
        {/* Introductie */}
        <div className="rounded-2xl bg-sage-50 border border-sage-200 px-5 py-4 flex flex-col gap-2.5">
          <p className="text-sm text-sage-800 leading-relaxed">
            Vink eenvoudig aan welke digitale diensten u gebruikt. Per dienst geeft u aan
            wat er na uw overlijden moet gebeuren.
          </p>
          <p className="text-sm text-sage-800 leading-relaxed">
            Gebruikt u een dienst die niet in de lijst staat? Dan kunt u deze eenvoudig zelf toevoegen.
          </p>
          <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 mt-1">
            <KeyRound className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-snug">
              Bewaar hier <strong>nooit wachtwoorden</strong>. Leg alleen vast wat ermee moet
              gebeuren en waar de toegangsgegevens te vinden zijn.
            </p>
          </div>
        </div>

        <InfoBlock title="Waarom dit nu vastleggen?" variant="neutral" defaultOpen={false}>
          <p>Een groot deel van ons leven speelt zich online af: e-mail, foto's, abonnementen, sociale media. Zonder enige aanwijzing komt niemand daar nog bij, en abonnementen lopen gewoon door. Bij Apple, Google en Facebook kunt u iemand aanwijzen die na uw overlijden toegang krijgt. Uw DigiD en MijnOverheid hoeft u niet zelf stop te zetten; die worden vanzelf geblokkeerd zodra de gemeente het overlijden verwerkt.</p>
          <p>Bewaar hier nooit wachtwoorden, alleen wat ermee moet gebeuren en waar de toegang te vinden is.</p>
        </InfoBlock>

        {/* Snelnavigatie */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => scrollTo(s.key)}
              className={`flex items-center gap-1.5 flex-shrink-0 rounded-full px-3 py-2 text-xs font-medium ${ACCENT[s.accent].chip}`}>
              {s.icon} {s.short}
            </button>
          ))}
        </div>

        {/* Secties met kaarten */}
        {SECTIONS.map(s => {
          const a = ACCENT[s.accent]
          const sugg = SUGGESTIONS[s.key] ?? []
          const entries = entriesFor(s.key).map(parseLine)
          const customEntries = entries.filter(e => !sugg.some(sg => nameMatches(e.name, sg)))

          // Eén dienstrij: checkbox "Ik gebruik deze dienst" + (indien aangevinkt) de keuzes.
          const renderRow = (name: string, removable: boolean) => {
            const entry = entries.find(e => nameMatches(e.name, name))
            const used = !!entry
            const { type, note } = entry ? parseAction(entry.action) : { type: '' as ActionType, note: '' }
            const nKey = `${s.key}::${name}`
            return (
              <div key={name} className="relative overflow-hidden rounded-xl border border-sand-200 bg-white">
                <span className={`absolute left-0 top-0 bottom-0 w-1 ${a.bar}`} aria-hidden="true" />
                <div className="flex items-center gap-3 pl-3 pr-2 py-2.5">
                  <button type="button" role="checkbox" aria-checked={used} aria-label={`Ik gebruik ${name}`}
                    onClick={() => toggleService(s.key, name, !used)}
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors ${used ? 'bg-moss border-moss text-white' : 'bg-white border-sand-300 hover:border-sage-400'}`}>
                    {used && <Check className="h-4 w-4" />}
                  </button>
                  <BrandLogo name={name} size={32} fallbackClassName={a.ring} />
                  <span className="flex-1 min-w-0 text-sm font-medium text-ink truncate">{name}</span>
                  {removable && (
                    <button type="button" onClick={() => removeService(s.key, name)} aria-label="Verwijderen"
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-ink-4 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {used && (
                  <div className="border-t border-sand-200 px-3 py-3 flex flex-col gap-2.5 bg-sand-50/40">
                    <p className="text-xs font-medium text-ink-3">Wat moet hiermee gebeuren?</p>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Wat moet hiermee gebeuren?">
                      {([['keep', 'Bewaren'], ['cancel', 'Opzeggen / verwijderen'], ['other', 'Anders']] as const).map(([t, lbl]) => {
                        const active = type === t
                        return (
                          <button key={t} type="button" role="radio" aria-checked={active}
                            onClick={() => { setAction(s.key, name, t, t === 'other' ? note : ''); setEditingNote(t === 'other' ? nKey : null) }}
                            className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-moss bg-moss text-white shadow-sm' : 'border-sand-300 bg-white text-ink-3 hover:border-sage-400 hover:text-ink-2'}`}>
                            <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${active ? 'border-white/70 bg-white/20' : 'border-sand-300'}`}>
                              {active && <Check className="h-3 w-3 text-white" />}
                            </span>
                            {lbl}
                          </button>
                        )
                      })}
                    </div>
                    {type === 'other' && (
                      editingNote === nKey ? (
                        <Textarea rows={2} maxLength={500} autoFocus
                          value={noteDraft[nKey] ?? note}
                          placeholder="Bijvoorbeeld: account bewaren voor mijn kinderen, eerst foto's downloaden, inloggegevens liggen in de kluis"
                          onChange={e => {
                            const v = e.target.value.replace(/[\r\n]+/g, ' ')   // één regel houden: opslag is regelgebaseerd
                            setNoteDraft(prev => ({ ...prev, [nKey]: v }))
                            setAction(s.key, name, 'other', v)
                          }}
                          onBlur={() => setEditingNote(null)} />
                      ) : (
                        <button type="button" onClick={() => setEditingNote(nKey)}
                          className="flex items-center gap-2.5 w-full text-left rounded-lg border border-sage-200 bg-sage-50 px-3 py-2.5 hover:border-sage-300 hover:bg-sage-100/50 transition-colors">
                          {(noteDraft[nKey] ?? note).trim() ? (
                            <>
                              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-moss text-white">
                                <Check className="h-3 w-3" aria-hidden="true" />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-xs font-semibold text-sage-800">Persoonlijke instructie opgeslagen</span>
                                <span className="block truncate text-xs text-ink-4">{(noteDraft[nKey] ?? note).trim()}</span>
                              </span>
                              <span className="flex items-center gap-1 flex-shrink-0 text-xs font-medium text-sage-700">
                                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />Bewerken
                              </span>
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 flex-shrink-0 text-sage-700" aria-hidden="true" />
                              <span className="flex-1 text-xs font-medium text-sage-700">Voeg een persoonlijke toelichting toe</span>
                            </>
                          )}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            )
          }

          return (
            <section key={s.key} id={`sec-${s.key}`} className="flex flex-col gap-2.5 scroll-mt-20">
              <div className="flex items-center gap-2">
                <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${a.chip}`}>{s.icon}</span>
                <h3 className="text-sm font-semibold text-ink">{s.label}</h3>
              </div>

              {s.key === 'websites' && (
                <div className="flex items-start gap-2.5 rounded-xl bg-blue-50 border border-blue-200 px-3 py-2.5">
                  <Landmark className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-900 leading-snug">
                    <strong>DigiD, MijnOverheid en de Berichtenbox</strong> worden na overlijden automatisch geblokkeerd zodra de gemeente het overlijden in de BRP registreert. Opzeggen of vastleggen is meestal niet nodig. Controleer wel of belangrijke berichten in MijnOverheid bekend zijn bij uw nabestaanden.
                  </p>
                </div>
              )}

              {sugg.map(name => renderRow(name, false))}
              {customEntries.map(e => renderRow(e.name, true))}

              {/* Eigen dienst toevoegen - zelfde keuzes als standaarddiensten */}
              <div className="flex gap-2">
                <input
                  value={customDraft[s.key] || ''}
                  onChange={e => setCustomDraft(prev => ({ ...prev, [s.key]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(s.key) } }}
                  placeholder="Andere dienst toevoegen…"
                  className="flex-1 min-w-0 rounded-xl border border-dashed border-sand-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-sage-500/20 focus:border-sage-500"
                />
                <button type="button" onClick={() => addCustom(s.key)}
                  disabled={!(customDraft[s.key] || '').trim()}
                  className="flex items-center gap-1.5 flex-shrink-0 rounded-xl bg-moss px-4 py-3 text-sm font-semibold text-white hover:bg-moss-light active:scale-[0.98] transition-all disabled:opacity-40 disabled:pointer-events-none">
                  <Plus className="h-4 w-4" /> Toevoegen
                </button>
              </div>
            </section>
          )
        })}

        <div className="rounded-2xl bg-blue-50 border border-blue-100 px-4 py-4 mt-2">
          <p className="text-sm font-semibold text-ink mb-1">Toegang vooraf regelen - geen wachtwoorden opslaan</p>
          <p className="text-xs text-ink-3 leading-relaxed mb-3">
            Sla hier geen wachtwoorden op. Leg alleen vast <em>waar</em> toegang geregeld is en wie de toegangscode
            kent (vertrouwenspersoon, partner, een document of elders). De grote platforms bieden officiële
            nalatenschapsregelingen:
          </p>
          <ul className="flex flex-col gap-1.5 text-sm">
            <li>
              <a href="https://support.apple.com/nl-nl/102631" target="_blank" rel="noopener noreferrer"
                className="text-sage-700 underline hover:text-sage-800">Apple - Erfeniscontact (Legacy Contact)</a>
            </li>
            <li>
              <a href="https://support.apple.com/nl-nl/guide/iphone/iph08022b194/ios" target="_blank" rel="noopener noreferrer"
                className="text-sage-700 underline hover:text-sage-800">Apple - Medische ID instellen</a>
            </li>
            <li>
              <a href="https://myaccount.google.com/inactive" target="_blank" rel="noopener noreferrer"
                className="text-sage-700 underline hover:text-sage-800">Google - Inactive Account Manager</a>
            </li>
            <li>
              <a href="https://support.google.com/android/answer/9319337" target="_blank" rel="noopener noreferrer"
                className="text-sage-700 underline hover:text-sage-800">Android - noodinformatie en noodcontacten</a>
            </li>
          </ul>
        </div>

        {status !== 'NOT_STARTED' && (
          <ClosingGuidance title="Uw digitale nalatenschap is voorbereid">
            Controleer af en toe of uw accounts, contactpersonen en digitale wensen nog actueel zijn.
          </ClosingGuidance>
        )}
        {!embedded && (
          <PrimaryActionButton onClick={async () => { await saveNow(); navigate(target) }} icon={<CheckCircle2 className="h-5 w-5" />}>
          {label}
        </PrimaryActionButton>
        )}
        {!embedded && <RegieCoachBlock page="digital" />}
      </ModuleShell>
    </div>
  )
}
